import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {WidgetConfiguration} from "./widget-configuration";
import {GraphicConfiguration} from "./graphic-configuration";

@JsonObject()
export class ToolBaseConfiguration {
    @JsonProperty()
    declare active?: boolean;

    constructor() {}
}
export class ElevationProfileVisibleElementsConfiguration {
    /**
     * When set to `false`, the legend (which includes statistics) is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare legend?: boolean;
    /**
     * When set to `false`, the chart is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare chart?: boolean;
    /**
     * When set to `false` the button used to clear the current elevation profile is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare clearButton?: boolean;
    /**
     * When set to `false`, the button used to open the settings popup is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare settingsButton?: boolean;
    /**
     * When set to `false`, the button used to start drawing/sketchinng is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare sketchButton?: boolean;
    /**
     * When set to `false`, the button used to select a path is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare selectButton?: boolean;
    /**
     * When set to `false`, the dropdown used to select the units is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare unitSelector?: boolean;
    /**
     * When set to `false`, the element used to toggle uniform chart scaling on or off is not displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#VisibleElements)
     */
    @JsonProperty()
    declare uniformChartScalingToggle?: boolean;
}
@JsonObject()
export class ElevationProfileConfiguration extends WidgetConfiguration {
    /**
     * When the spatial reference is projected (other than web mercator) and the path is shorter than this threshold, distances will be computed planimetrically.
     *
     * @default 100000
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#geodesicDistanceThreshold)
     */
    @JsonProperty()
    declare geodesicDistanceThreshold?: number;
    /**
     * The widget's default CSS icon class.
     *
     * @deprecated since 4.27. Use {@link module:esri/widgets/ElevationProfile#icon icon} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#iconClass)
     */
    @JsonProperty()
    declare iconClass?: string;
    /**
     * The input line graphic along which elevation will be queried in order to generate an elevation profile.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#input)
     */
    @JsonProperty()
    declare input?: GraphicConfiguration;
    /**
     * Collection of elevation profile lines which are to be generated and displayed in the widget's chart.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#profiles)
     */
    // profiles?: CollectionProperties<
    //    | (ElevationProfileLineGroundProperties & { type: "ground" })
    //    | (ElevationProfileLineInputProperties & { type: "input" })
    //    | (ElevationProfileLineQueryProperties & { type: "query" })
    //    | (ElevationProfileLineViewProperties & { type: "view" })
    //   >;
    /**
     * Unit system (imperial, metric) or specific unit used for displaying the elevation and distance values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#unit)
     */
    @JsonProperty()
    declare unit?: | "imperial"
    | "metric"
    | "millimeters"
    | "centimeters"
    | "decimeters"
    | "meters"
    | "kilometers"
    | "inches"
    | "feet"
    | "yards"
    | "miles"
    | "nautical-miles"
    | "us-feet";
    /**
     * List of available units and unit systems (imperial, metric) for displaying the elevation and distance values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#unitOptions)
     */
    @JsonProperty()
    declare unitOptions?: (
        | "imperial"
        | "metric"
        | "millimeters"
        | "centimeters"
        | "decimeters"
        | "meters"
        | "kilometers"
        | "inches"
        | "feet"
        | "yards"
        | "miles"
        | "nautical-miles"
        | "us-feet"
    )[];
    /**
     * A reference to the [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html) or the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#view)
     */
    // @JsonProperty()
    // declare view?: (SceneViewProperties & { type: "3d" }) | (MapViewProperties & { type: "2d" });
    /**
     * The view model for this widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#viewModel)
     */
    // @JsonProperty()
    // declare viewModel?: ElevationProfileViewModelProperties;
    /**
     * This property provides the ability to display or hide the individual elements of the widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-ElevationProfile.html#visibleElements)
     */
    @JsonProperty()
    declare visibleElements?: ElevationProfileVisibleElementsConfiguration;
}
@JsonObject()
export class ToolsConfiguration {
    @JsonProperty()
    declare profile?: ElevationProfileConfiguration;

    constructor() {}
}
